/**
 * Shared utilities for document rendering.
 *
 * Extracted from PreviewPanel.tsx and DocumentRenderer.tsx to eliminate
 * code duplication across the three rendering codepaths:
 * - PreviewPanel (Editor/Web — store-connected)
 * - DocumentRenderer (Play — store-free, prop-driven)
 * - ForBlockRenderer (for-loop iterations)
 */

import type { ContentDefinition, PluginOutput } from "@magic-md/core";
import {
    getContentMemberValue,
    parseMagicMd,
    splitScopedMemberPath,
} from "@magic-md/core";
import { createElement } from "react";
export { groupBlocks } from "../../lib/document-render-logic";

const HTML_COMMENT_RE = /<!--[\s\S]*?-->/g;

export function normalizeRenderedMarkdown(markdown: string): string {
    return markdown.replace(HTML_COMMENT_RE, "").replace(/\\(::[a-z])/g, "$1");
}

/* ── resolveRefValue — navigate nested output path ── */

/**
 * Resolve a `$ref{}` output path into a value.
 *
 * Supports dot notation (`output.field.nested`) and bracket array indices
 * (`output.items[0]`).
 */
export function resolveRefValue(
    definitionId: string,
    accessor: "output" | "body",
    outputPath: string,
    outputs: Map<string, PluginOutput>,
    contentDefinitions?: Map<string, ContentDefinition>,
): { resolved: boolean; value: unknown } {
    if (accessor === "body") {
        const contentDef = contentDefinitions?.get(definitionId);
        if (contentDef) {
            if (outputPath) return { resolved: false, value: undefined };
            return { resolved: true, value: contentDef.body };
        }

        const scoped = splitScopedMemberPath(
            definitionId,
            (id) => !!contentDefinitions?.has(id),
            (id) => !!contentDefinitions?.has(id) || outputs.has(id),
        );
        const memberContent = scoped
            ? findNestedContentDefinition(contentDefinitions?.get(scoped.rootId), scoped.memberPath)
            : null;
        if (!memberContent) return { resolved: false, value: undefined };
        if (outputPath) return { resolved: false, value: undefined };
        return { resolved: true, value: memberContent.body };
    }

    let output = outputs.get(definitionId) ?? contentDefinitions?.get(definitionId)?.output;
    if (output === undefined) {
        const scoped = splitScopedMemberPath(
            definitionId,
            (id) => !!contentDefinitions?.has(id),
            (id) => !!contentDefinitions?.has(id) || outputs.has(id),
        );
        if (scoped) {
            const rootOutput = contentDefinitions?.get(scoped.rootId)?.output;
            output = getContentMemberValue(rootOutput, scoped.memberPath) as PluginOutput | undefined;
        }
    }
    if (output === undefined) return { resolved: false, value: undefined };

    if (!outputPath) return { resolved: true, value: output };

    const segments: string[] = [];
    const pathPattern = /([\w-]+)|\[(\d+)\]/g;
    let seg = pathPattern.exec(outputPath);
    while (seg !== null) {
        segments.push(seg[1] || seg[2]);
        seg = pathPattern.exec(outputPath);
    }

    let current: unknown = output;
    for (const key of segments) {
        if (current === null || current === undefined) return { resolved: false, value: undefined };
        if (Array.isArray(current)) {
            current = current[parseInt(key, 10)];
        } else if (typeof current === "object") {
            current = (current as Record<string, unknown>)[key];
        } else {
            return { resolved: false, value: undefined };
        }
    }
    return { resolved: true, value: current };
}

function findNestedContentDefinition(
    root: ContentDefinition | undefined,
    memberPath: readonly string[],
): ContentDefinition | null {
    let current = root;
    for (const member of memberPath) {
        if (!current) return null;
        let parsed: ReturnType<typeof parseMagicMd>;
        try {
            parsed = parseMagicMd(current.body);
        } catch {
            return null;
        }
        current = parsed.contentDefinitions?.get(member);
    }
    return current ?? null;
}

/**
 * Format a resolved ref value for inline display.
 */
export function formatRefValue(value: unknown): string {
    if (value === null) return "null";
    if (value === undefined) return "undefined";
    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    return JSON.stringify(value);
}

/* ── createTocHeadingComponents — heading components with TOC ids ── */

/**
 * Create custom heading components for ReactMarkdown that add `data-toc-id`
 * attributes. Uses a mutable counter — safe because ReactMarkdown renders
 * synchronously within a single render pass.
 *
 * Results are cached by blockIndex so the same component references are
 * returned across re-renders, preventing React from unmounting/remounting
 * heading elements unnecessarily.
 */
const tocCounters = new Map<number, { current: number }>();
const tocComponentCache = new Map<
    number,
    Record<string, React.FC<React.HTMLAttributes<HTMLHeadingElement>>>
>();

export function createTocHeadingComponents(blockIndex: number) {
    // Get or create counter for this blockIndex
    let counter = tocCounters.get(blockIndex);
    if (!counter) {
        counter = { current: 0 };
        tocCounters.set(blockIndex, counter);
    }
    // Always reset — each render pass starts from 0
    counter.current = 0;

    // Return cached components if available (they close over the same counter ref)
    const cached = tocComponentCache.get(blockIndex);
    if (cached) return cached;

    // Create component functions — they capture `counter` by reference
    const components: Record<
        string,
        React.FC<React.HTMLAttributes<HTMLHeadingElement>>
    > = {};
    for (let level = 1; level <= 6; level++) {
        const tag = `h${level}`;
        components[tag] = ({ children, ...props }) => {
            const tocId = `toc-h-${blockIndex}-${counter.current++}`;
            return createElement(tag, { ...props, "data-toc-id": tocId }, children);
        };
    }

    // Limit cache size to prevent memory leaks in long sessions
    if (tocComponentCache.size > 500) {
        const firstKey = tocComponentCache.keys().next().value;
        if (firstKey !== undefined) {
            tocComponentCache.delete(firstKey);
            tocCounters.delete(firstKey);
        }
    }
    tocComponentCache.set(blockIndex, components);

    return components;
}

/* ── groupBlocks — group inline blocks into paragraph-level arrays ── */
